// Bot Status Monitor - Real-time bot activity and status display
class BotStatusMonitor {
    constructor(containerId) {
        this.containerId = containerId;
        this.container = null;
        this.botStatus = {};
        this.activityLog = [];
        this.maxLogEntries = 20;
        
        this.init();
    }

    /**
     * Initialize the bot status monitor
     */
    init() {
        this.container = document.getElementById(this.containerId);
        if (!this.container) {
            console.error(`Bot status monitor container ${this.containerId} not found`);
            return;
        }

        this.render();
        this.setupEventListeners();
    }

    /**
     * Render the bot status monitor structure
     */
    render() {
        this.container.innerHTML = `
            <div class="bot-status-monitor">
                <div class="bot-overview">
                    <div class="bot-info">
                        <div class="bot-name">Order Cancellation Bot</div>
                        <div class="bot-status-display">
                            <span class="status-indicator" id="bot-status-indicator">●</span>
                            <span class="status-text" id="bot-status-text">Inactive</span>
                        </div>
                    </div>
                    
                    <div class="bot-stats">
                        <div class="stat-item">
                            <span class="stat-value" id="tracked-count">0</span>
                            <span class="stat-label">Tracked</span>
                        </div>
                        <div class="stat-item">
                            <span class="stat-value" id="cancel-timeout">3h</span>
                            <span class="stat-label">Timeout</span>
                        </div>
                    </div>
                </div>

                <div class="bot-activity">
                    <div class="activity-header">
                        <h4>Recent Activity</h4>
                        <button class="clear-log-btn" id="clear-log">Clear</button>
                    </div>
                    
                    <div class="activity-log" id="activity-log">
                        <div class="log-entry placeholder">
                            <span class="log-time">--:--:--</span>
                            <span class="log-message">Waiting for bot activity...</span>
                        </div>
                    </div>
                </div>

                <div class="bot-controls">
                    <button class="bot-control-btn" id="toggle-bot">
                        <span class="btn-icon">⏯️</span>
                        <span class="btn-text">Toggle Bot</span>
                    </button>
                    <button class="bot-control-btn" id="force-check">
                        <span class="btn-icon">🔍</span>
                        <span class="btn-text">Force Check</span>
                    </button>
                </div>
            </div>
        `;
    }

    /**
     * Setup event listeners
     */
    setupEventListeners() {
        // Clear log button
        const clearLogBtn = this.container.querySelector('#clear-log');
        if (clearLogBtn) {
            clearLogBtn.addEventListener('click', () => {
                this.clearActivityLog();
            });
        }

        // Toggle bot button
        const toggleBtn = this.container.querySelector('#toggle-bot');
        if (toggleBtn) {
            toggleBtn.addEventListener('click', () => {
                this.toggleBot();
            });
        }

        // Force check button
        const forceCheckBtn = this.container.querySelector('#force-check');
        if (forceCheckBtn) {
            forceCheckBtn.addEventListener('click', () => {
                this.forceCheck();
            });
        }
    }

    /**
     * Update bot status
     */
    updateStatus(status) {
        const previousEnabled = this.botStatus.enabled;
        this.botStatus = status || {};
        this.renderStatus(previousEnabled);
    }

    /**
     * Render bot status display
     */
    renderStatus(previousEnabled = undefined) {
        const statusIndicator = this.container.querySelector('#bot-status-indicator');
        const statusText = this.container.querySelector('#bot-status-text');
        const trackedCount = this.container.querySelector('#tracked-count');
        const cancelTimeout = this.container.querySelector('#cancel-timeout');

        if (statusIndicator && statusText) {
            if (this.botStatus.enabled) {
                statusIndicator.className = 'status-indicator active';
                statusText.textContent = 'Active';
            } else {
                statusIndicator.className = 'status-indicator inactive';
                statusText.textContent = 'Inactive';
            }
        }

        if (trackedCount) {
            trackedCount.textContent = this.botStatus.trackedOrdersCount || 0;
        }

        if (cancelTimeout) {
            const hours = this.botStatus.cancelAfterHours || 3;
            cancelTimeout.textContent = `${hours}h`;
        }

        // Only log status changes, not repeated status updates
        if (this.botStatus.enabled !== undefined && previousEnabled !== this.botStatus.enabled) {
            this.addActivity('status-update', `Bot ${this.botStatus.enabled ? 'enabled' : 'disabled'}`);
        }
    }

    /**
     * Add activity to log
     */
    addActivity(type, message, data = null) {
        const timestamp = new Date();
        const entry = {
            type,
            message,
            data,
            timestamp,
            id: Date.now() + Math.random()
        };

        this.activityLog.unshift(entry);
        
        // Keep only recent entries
        if (this.activityLog.length > this.maxLogEntries) {
            this.activityLog = this.activityLog.slice(0, this.maxLogEntries);
        }

        this.renderActivityLog();
    }

    /**
     * Render activity log
     */
    renderActivityLog() {
        const logContainer = this.container.querySelector('#activity-log');
        if (!logContainer) return;

        if (this.activityLog.length === 0) {
            logContainer.innerHTML = `
                <div class="log-entry placeholder">
                    <span class="log-time">--:--:--</span>
                    <span class="log-message">Waiting for bot activity...</span>
                </div>
            `;
            return;
        }

        const logHTML = this.activityLog.map(entry => {
            const timeStr = entry.timestamp.toLocaleTimeString();
            const iconMap = {
                'order-tracked': '📝',
                'order-cancelled': '❌',
                'check-cycle': '🔍',
                'status-update': '⚙️',
                'error': '⚠️',
                'info': 'ℹ️'
            };
            const icon = iconMap[entry.type] || '•';

            return `
                <div class="log-entry ${entry.type}">
                    <span class="log-time">${timeStr}</span>
                    <span class="log-icon">${icon}</span>
                    <span class="log-message">${entry.message}</span>
                </div>
            `;
        }).join('');

        logContainer.innerHTML = logHTML;
        
        // Auto-scroll to top for newest entries
        logContainer.scrollTop = 0;
    }

    /**
     * Clear activity log
     */
    clearActivityLog() {
        this.activityLog = [];
        this.renderActivityLog();
    }

    /**
     * Toggle bot status
     */
    async toggleBot() {
        try {
            if (window.electronAPI) {
                const currentEnabled = this.botStatus.enabled || false;
                const result = await window.electronAPI.invoke('bot:toggleCancellation', !currentEnabled);
                
                if (result.success) {
                    this.addActivity('status-update', `Bot ${result.enabled ? 'enabled' : 'disabled'}`);
                    this.botStatus.enabled = result.enabled;
                    this.renderStatus();
                } else {
                    this.addActivity('error', `Failed to toggle bot: ${result.error}`);
                }
            }
        } catch (error) {
            console.error('Failed to toggle bot:', error);
            this.addActivity('error', 'Failed to toggle bot status');
        }
    }

    /**
     * Force check cycle
     */
    async forceCheck() {
        try {
            this.addActivity('check-cycle', 'Manual check cycle initiated...');
            
            if (window.electronAPI) {
                // Trigger actual bot check cycle, not just status refresh
                const result = await window.electronAPI.invoke('bot:forceCheckCycle');
                if (result && result.success) {
                    this.addActivity('check-cycle', result.message || 'Check cycle completed');
                    // Refresh status after forced check
                    setTimeout(() => this.refreshStatus(), 1000);
                } else {
                    // Fallback to status refresh if forceCheckCycle not available
                    const statusResult = await window.electronAPI.invoke('bot:getCancellationStatus');
                    if (statusResult.success) {
                        this.updateStatus(statusResult.status);
                        this.addActivity('check-cycle', `Status refreshed - ${statusResult.status.trackedOrdersCount || 0} orders tracked`);
                    } else {
                        this.addActivity('error', `Check failed: ${statusResult.error}`);
                    }
                }
            } else {
                this.addActivity('error', 'Bot API not available');
            }
            
        } catch (error) {
            console.error('Failed to force check:', error);
            this.addActivity('error', 'Failed to initiate manual check');
        }
    }

    /**
     * Refresh bot status
     */
    async refreshStatus() {
        try {
            if (window.electronAPI) {
                const result = await window.electronAPI.invoke('bot:getCancellationStatus');
                if (result.success) {
                    this.updateStatus(result.status);
                } else {
                    console.error('Failed to get bot status:', result.error);
                }
            }
        } catch (error) {
            console.error('Failed to refresh bot status:', error);
        }
    }

    /**
     * Handle bot notification from main process
     */
    handleNotification(notification) {
        const { type, data } = notification;
        
        switch (type) {
            case 'order-tracked':
                this.addActivity('order-tracked', `Tracking ${data.direction} ${data.asset} @ $${data.entryPrice}`);
                break;
            case 'order-cancelled':
                this.addActivity('order-cancelled', `Cancelled ${data.direction} ${data.asset} (timeout)`);
                break;
            case 'order-checked':
                this.addActivity('check-cycle', `Checked ${data.direction} ${data.asset} - ${data.timeRemaining || 'active'}`);
                break;
            case 'bot-started':
                this.addActivity('status-update', 'Bot monitoring started');
                break;
            case 'bot-stopped':
                this.addActivity('status-update', 'Bot monitoring stopped');
                break;
            case 'check-cycle-complete':
                this.addActivity('check-cycle', `Check cycle: ${data.checked || 0} orders, ${data.cancelled || 0} cancelled`);
                break;
            case 'bot-toggle':
                this.addActivity('status-update', data.message);
                break;
            default:
                if (data && data.message) {
                    this.addActivity('info', data.message);
                }
        }
        
        // Refresh status after notification
        setTimeout(() => {
            this.refreshStatus();
        }, 500);
    }

    /**
     * Simulate periodic activity (for demo purposes)
     */
    simulateActivity() {
        const activities = [
            { type: 'check-cycle', message: 'Checking tracked orders...' },
            { type: 'check-cycle', message: 'Polling user open orders from API' },
            { type: 'info', message: 'All tracked orders still open' },
            { type: 'check-cycle', message: 'Next check in 60 seconds' }
        ];

        let index = 0;
        const interval = setInterval(() => {
            if (index < activities.length && this.botStatus.enabled) {
                const activity = activities[index];
                this.addActivity(activity.type, activity.message);
                index++;
            } else {
                clearInterval(interval);
            }
        }, 2000);
    }

    /**
     * Cleanup
     */
    destroy() {
        // Cleanup if needed
    }
}

// Export for global access
window.BotStatusMonitor = BotStatusMonitor;
export { BotStatusMonitor };
